import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { api, formatEUR } from "@/lib/api";

const FINISHES_MAP = {
  AURA: { default: "Ancient Sand", options: ["Ancient Sand", "Moonstone", "Ash Rock"] },
  MONOLITH: { default: "Moonstone", options: ["Moonstone", "Ancient Sand", "Ash Rock"] },
  AMMON: { default: "Ancient Sand", options: ["Ancient Sand", "Moonstone", "Ash Rock"] },
  CRATERA: { default: "Ancient Sand", options: ["Ancient Sand", "Moonstone", "Ash Rock"] },
  FRACTAL: { default: "Ash Rock", options: ["Ash Rock", "Moonstone", "Ancient Sand"] },
};

const AddToCartButton = ({ product, selectedFinish }) => {
  const { add } = useCart();
  return (
    <button 
      onClick={() => add(product.id, selectedFinish)} 
      data-testid={`add-to-cart-${product.id}`} 
      className="purchase-btn w-full"
    >
      <span className="relative z-10">Add to Cart</span>
      <span className="relative z-10 text-sand">{formatEUR(product.price)}</span>
    </button>
  );
};

export const ProductCard = ({ product, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  // Offset ridotto a md:mt-8 per mantenere il ritmo sfalsato senza creare buchi vuoti in basso
  const offset = index % 2 === 1 ? "md:mt-8" : "";

  const productConfig = FINISHES_MAP[product.name?.toUpperCase()] || {
    default: "Ancient Sand",
    options: ["Ancient Sand", "Moonstone", "Ash Rock"]
  };

  const [selectedFinish, setSelectedFinish] = useState(productConfig.default);

  return (
    <>
      <motion.article
        initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.12 }}
        className={`group flex flex-col ${offset}`} data-testid={`product-card-${product.id}`}
      >
        <motion.div
          variants={{ hidden: { clipPath: "inset(10% 10% 10% 10%)" }, show: { clipPath: "inset(0% 0% 0% 0%)" } }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative aspect-[4/5] overflow-hidden bg-slate spotlight-frame cursor-zoom-in"
          onClick={() => setIsOpen(true)}
        >
          <motion.img
            src={product.image} alt={`${product.name} — ${product.variant}`} loading="lazy"
            variants={{ hidden: { scale: 1.25 }, show: { scale: 1 } }}
            transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]"
          />
          <span className="absolute top-5 left-5 label text-bone/80">No. {product.number}</span>
          <span className="absolute bottom-5 right-5 label text-bone/70">{product.dimensions}</span>
        </motion.div>

        <motion.div
          variants={{ hidden: { opacity: 0.001, y: 24 }, show: { opacity: 1, y: 0 } }}
          transition={{ duration: 1.2, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="pt-6 md:pt-8 flex flex-col justify-between flex-grow"
        >
          <div>
            <h3 className="font-serif text-bone text-3xl md:text-4xl font-light leading-[1.05]" data-testid={`product-name-${product.id}`}>
              {product.name} <span className="text-stone">—</span> <em className="font-light">{product.variant}</em>
            </h3>
            <p className="body mt-4 max-w-md">{product.description}</p>
            
            <div className="mt-5">
              <p className="label text-[10px] text-bone/60 uppercase tracking-widest mb-2.5">
                Finish Edition
              </p>
              <div className="flex flex-wrap gap-2">
                {productConfig.options.map((finish) => (
                  <button
                    key={finish}
                    type="button"
                    onClick={() => setSelectedFinish(finish)}
                    className={`text-xs font-mono px-3 py-1.5 border transition-all duration-300 ${
                      selectedFinish === finish
                        ? "border-sand text-bone bg-sand/10"
                        : "border-bone/15 text-bone/50 hover:border-bone/40 hover:text-bone"
                    }`}
                  >
                    {finish} {finish === productConfig.default ? "(Pictured)" : ""}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6"><AddToCartButton product={product} selectedFinish={selectedFinish} /></div>
        </motion.div>
      </motion.article>

      {/* LIGHTBOX / FULLSCREEN MODAL */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate/95 backdrop-blur-md p-4 md:p-12 cursor-zoom-out"
            onClick={() => setIsOpen(false)}
          >
            <button
              className="absolute top-6 right-8 text-bone/60 hover:text-bone text-xs tracking-[0.2em] uppercase font-mono z-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              [ Close ]
            </button>
            <motion.img
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={product.image}
              alt={`${product.name} — ${product.variant}`}
              className="max-w-full max-h-[90vh] object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};